import { useId, useRef } from 'react';
import { Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { RwButton, RwModal } from '@/common/presentation/components/rw';

export interface UnarchiveBlockDialogProps {
  open: boolean;

  blockName: string;

  isRestoring: boolean;

  onClose: () => void;

  onConfirm: () => void;
}

/**
 * Restore confirmation (dev-only flow — the designer prototype this epic ports from has no
 * restore at all). Restyled on `RwModal` for LLAMS-617 Part 3; logic unchanged from the prior MUI
 * Dialog version.
 */
export function UnarchiveBlockDialog({ open, blockName, isRestoring, onClose, onConfirm }: UnarchiveBlockDialogProps) {
  const descriptionId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const context = `"${blockName}" will become active again and appear in the default block library list.`;

  return (
    <RwModal
      open={open}
      onClose={() => {
        if (!isRestoring) onClose();
      }}
      title="Restore this block?"
      context={context}
      dismissable={!isRestoring}
      initialFocusRef={cancelRef}
      testId="document-builder:unarchive-block-dialog:root"
      descriptionId={descriptionId}
      footer={
        <>
          <RwButton
            ref={cancelRef}
            tier="secondary"
            onClick={onClose}
            disabled={isRestoring}
            data-testid="document-builder:unarchive-block-dialog:cancel"
          >
            Cancel
          </RwButton>

          <RwButton
            tier="primary"
            onClick={onConfirm}
            disabled={isRestoring}
            loading={isRestoring}
            data-testid="document-builder:unarchive-block-dialog:confirm"
            aria-label="Restore block"
          >
            Restore
          </RwButton>
        </>
      }
    >
      <Box component="span" id={descriptionId} sx={visuallyHidden}>
        {context}
      </Box>
    </RwModal>
  );
}
